import { observer } from 'mobx-react-lite'
import { FilterControlsContainer } from '@/components/FilterControls/FilterControlsContainer'
import { useToggle } from '@/hooks/state-hooks'
import { VirtualTableContainer } from '@/components/VirtualTableContainer'
import { type AdminTransactionHistoryViewModel } from '@/features/payouts/admin/transaction-history/AdminTransactionHistoryViewModel'
import { type AdminTransaction } from '@/features/payouts/admin/AdminTransaction'
import {
  HistoricalTransactionRow,
  HistoricalTransactionsTable,
  HistoricalTransactionsTableHeader,
} from '@/features/payouts/admin/transaction-history/components/HistoricalTransactionsTable'

export interface HistoricalTransactionsTableContainerProps {
  vm: AdminTransactionHistoryViewModel
}

export const HistoricalTransactionsTableContainer = observer(
  function HistoricalTransactionsTableContainer({
    vm,
  }: HistoricalTransactionsTableContainerProps) {
    const filtersShowing = useToggle(false)

    return (
      <HistoricalTransactionsTable
        searchText={vm.searchText.value}
        onSearchTextChange={vm.searchText.change}
        filtersShowing={filtersShowing.isEnabled}
        onToggleFilters={filtersShowing.toggle}
        appliedFilterCount={vm.filters.appliedFilterCount}
        isExportPending={vm.exportCsv.pending}
        onExportCsv={vm.exportCsv}
        filterControls={
          <FilterControlsContainer
            vm={vm.filters}
            active={filtersShowing.isEnabled}
            editorTestId="job"
            testId="job-filter"
          />
        }
        showTable={vm.showTable}
        appliedFilters={vm.filtersApplied}
        onClearFilters={vm.clearFilters}
      >
        <VirtualTableContainer
          data={vm.transactions}
          overscan={400}
          scroller={vm.transactionsScroller}
          itemContent={(_index, transaction) => (
            <HistoricalTransactionRowContainer transaction={transaction} />
          )}
          headerContent={() => <HistoricalTransactionsTableHeader />}
        />
      </HistoricalTransactionsTable>
    )
  },
)

interface HistoricalTransactionRowContainerProps {
  transaction: AdminTransaction
}

const HistoricalTransactionRowContainer = observer(
  function HistoricalTransactionRowContainer({
    transaction,
  }: HistoricalTransactionRowContainerProps) {
    return (
      <HistoricalTransactionRow
        id={transaction.id}
        source={transaction.source}
        memo={transaction.memo}
        reason={transaction.reason}
        jobId={transaction.jobId ?? null}
        recipientName={transaction.recipient.displayName}
        recipientAvatar={transaction.recipient.avatar}
        recipientAvatarFallback={transaction.recipient.avatarFallback}
        amount={transaction.amount}
        workspaceName={transaction.workspace.name}
        workspaceIcon={transaction.workspace.branding.logos.icon}
        shortId={transaction.shortId}
        completedAt={transaction.completedAt}
        stripeAccountId={transaction.stripeAccountId}
        flags={transaction.flags.slice()}
      />
    )
  },
)
